import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { graphConfig } from '../authConfig';
import SignInButton from './SignInButton';
import styled from 'styled-components';

const AuthGuard = ({ children }) => {
  const { instance, accounts } = useMsal();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkUserAuthorization = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Get user's groups
      const groupsResponse = await instance.acquireTokenSilent({
        scopes: ["GroupMember.Read.All"],
        account: accounts[0]
      });

      const groups = await fetch(graphConfig.graphGroupsEndpoint, {
        headers: {
          'Authorization': `Bearer ${groupsResponse.accessToken}`
        }
      }).then(response => response.json());

      // Check if user is in 'Founders' group
      const isInFoundersGroup = groups.value.some(group => 
        group.displayName === 'Founders' || group.id === 'fd9dae47-3ff3-4906-b2e2-929134d4f55f'
      );

      if (isInFoundersGroup) {
        setIsAuthorized(true);
      } else {
        setIsAuthorized(false);
      }
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenPopup({
          scopes: ["User.Read", "GroupMember.Read.All"]
        });
        checkUserAuthorization();
      } else {
        console.error('Authorization check failed:', error);
        setIsAuthorized(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (accounts.length > 0) {
      checkUserAuthorization();
    } else {
      setIsLoading(false);
    }
  }, [accounts, checkUserAuthorization]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Checking authorization...</LoadingText>
      </LoadingContainer>
    );
  }

  if (!accounts.length) {
    return (
      <AuthContainer>
        <AuthCard>
          <h2>Welcome to TUT HubSpot Graph</h2>
          <p>Please sign in to access the application.</p>
          <SignInButton />
        </AuthCard>
      </AuthContainer>
    );
  }

  if (!isAuthorized) {
    return (
      <AuthContainer>
        <AuthCard>
          <h2>Access Denied</h2>
          <p>You do not have permission to access this application. Only members of the 'Founders' group can access this resource.</p>
          <p>If you believe this is an error, please contact your administrator.</p>
        </AuthCard>
      </AuthContainer>
    );
  }

  return children;
};

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0078d4;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 16px;
`;

const AuthContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #f5f5f5;
`;

const AuthCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 400px;

  h2 {
    margin: 0 0 20px 0;
    color: #333;
  }

  p {
    margin: 0 0 20px 0;
    color: #666;
    line-height: 1.5;
  }
`;

export default AuthGuard;
